import { MeetingProvider, useAuthContext } from "@kalyzee/kast-web-sdk";
import { ROLE } from "@kalyzee/kast-web-sdk/http";
import { useURL } from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import React, { useEffect, useMemo } from "react";
import { Route } from "react-router-dom";
import ImpersonationBar from "../components/common/ImpersonationBar";
import GuestFallbackRoute from "../components/navigation/GuestFallback";
import { generateRoute, PageComponent } from "../components/navigation/Page";
import { COMPOSITION_ROOT_PATH, PAGE_PATHS, PageMasterMode, PageSlaveMode } from "../components/navigation/PageContext";
import PrivateRoute from "../components/navigation/PrivateRoute";
import PublicRoute from "../components/navigation/PublicRoute";
import MasterRouteContainer from "../components/navigation/routes/MasterRouteContainer";
import SlaveRouteContainer from "../components/navigation/routes/SlaveRouteContainer";
import ConnexionView from "../components/utils/ConnexionView";
import { useAppStoreContext } from "../contexts/appStore/context";
import { SessionMode, StoreUtilsHooks } from "../contexts/appStore/hooks";
import { useAppStoreSelector } from "../contexts/appStore/selectors";
import { UserCardProvider } from "../contexts/userCard/context";
import { logger as globalLogger, useLogger } from "../helpers/logger";
import { appMessage } from "../helpers/message";
import {
  AppNavigate,
  getGlobalQuery,
  ListVodRedirect,
  LoginRedirect,
  NavigateGlobalQuery,
  NavigateSlave,
  PublicMeetingRedirect,
  PublicRedirect,
  useAuthRedirects,
} from "../helpers/navigation";
import { resolveMediaEventsEndpoint } from "../helpers/mediaEvents";
import { buildRetransmissionMediaStateExtractor, buildRetransmissionSrcExtractor, RetransmissionTemplateError } from "../helpers/retransmission";
import { buildSdpFormatters, SdpPipelineError } from "../helpers/sdp";
import { getStoredSettings, Settings } from "../helpers/settings";
import { toastError } from "../helpers/toast";
import { meetingsContext } from "../hooks/meeting";
import { useSettings } from "../hooks/settings";
import { JoinPage } from "../pages/master/student/join/join";
import { MeetingListStudentPage } from "../pages/master/student/list/list";
import { MeetingDashboardStudentPage } from "../pages/master/student/meeting/connected/dashboard";
import ForbiddenPage from "../pages/public/forbidden/forbidden";
import LoginPage from "../pages/public/login/login";
import LogoutPage from "../pages/public/logout/logout";
import MeetingSlavePage from "../pages/slave/meeting/meeting";

import { AppLoadingView } from "../components/utils/AppLoadingView";
import { LoadingView } from "../components/utils/LoadingView";
import { useSdkContext } from "../contexts/sdk/context";
import { MeetingLobbyPage } from "../pages/master/student/lobby/lobby";
import { MeetingInitPage } from "../pages/master/student/meeting/init";
import { CompositionPage } from "../pages/master/common/composition/composition";
import { MeetingPlayerPage } from "../pages/master/student/player/player";
import styles from "./App.module.css";

/**
 * The source wall. Its id is optional (`/composition` = the first configured
 * composition), which React Router's optional segments cannot express without
 * breaking `getPageUrl`'s param substitution — hence the extra bare route.
 * Registered in both trees: the page reads the settings and opens local
 * devices, and calls no API.
 */
const compositionRoutes = () => {
  const element = <PageComponent mode={SessionMode.MASTER} page={PageMasterMode.COMPOSITION} element={<CompositionPage />} />;
  return [
    <Route key="composition-id" path={PAGE_PATHS[SessionMode.MASTER][PageMasterMode.COMPOSITION]} element={element} />,
    <Route key="composition" path={COMPOSITION_ROOT_PATH} element={element} />,
  ];
};

// One report per destination/field/missing-keys combination: the templates are
// resolved again at every publish attempt. Cleared when the config changes.
const reportedRetransmissionErrors = new Set<string>();

/**
 * A retransmission destination whose url/srcKey template could not be resolved: the
 * stream is not republished at all. The user only gets a short toast; everything
 * needed to fix the config (destination, template, missing keys, partial
 * substitution, context) goes to the console.
 */
const reportRetransmissionTemplateError = (error: RetransmissionTemplateError) => {
  const key = `${error.destinationId}:${error.field}:${error.unresolved.join(",")}`;
  if (reportedRetransmissionErrors.has(key)) return;
  reportedRetransmissionErrors.add(key);
  globalLogger.error("[retransmission] unresolved template", error);
  toastError("La retransmission n'a pas pu démarrer." /* TRANSLATION */);
};

// One report per side/message: the pipelines run again at every negotiation.
// Cleared when the configured formatter changes.
const reportedSdpFormatterErrors = new Set<string>();

/**
 * An SDP formatter pipeline that could not be applied. A pipeline rejected upfront
 * publishes an unformatted SDP; one failing mid-run publishes a partially formatted
 * one — either way the stream goes out, which is why this only warns.
 */
const reportSdpFormatterError = (error: SdpPipelineError) => {
  const key = `${error.field}:${error.message}`;
  if (reportedSdpFormatterErrors.has(key)) return;
  reportedSdpFormatterErrors.add(key);
  globalLogger.error("[sdpFormatter] pipeline not applied", error);
  toastError(`Le formatteur SDP (${error.field}) n'a pas pu être appliqué.` /* TRANSLATION */);
};

function App() {
  const url = useURL();
  const store = useAppStoreContext();

  const mode = useAppStoreSelector((state) => state.mode);
  const user = useAppStoreSelector((state) => state.user);
  const userFetchStatus = useAppStoreSelector((state) => state.userFetchStatus);

  const fetchUser = StoreUtilsHooks.useFetchUser(store);

  const [settings, setSettings] = useSettings();
  const logger = useLogger();
  const { status: authStatus } = useAuthContext();

  const sdkCtx = useSdkContext();

  useAuthRedirects();

  useEffect(() => {
    if (getGlobalQuery(url.href, NavigateGlobalQuery.SLAVE) && mode !== SessionMode.SLAVE) {
      store.dispatch("setMode", SessionMode.SLAVE);
    } else if (!mode) store.dispatch("setMode", SessionMode.MASTER);
    const screenIndex = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_INDEX);
    const screenName = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_NAME);
    const screenLayoutId = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_LAYOUT_ID);
    if (screenIndex !== undefined || screenName || screenLayoutId !== undefined) {
      store.dispatch("setScreen", {
        index: screenIndex,
        name: screenName,
        layoutId: screenLayoutId,
      });
    }
    const disableGoogleLogin = getGlobalQuery(url.href, NavigateGlobalQuery.DISABLE_GOOGLE_LOGIN);
    if (disableGoogleLogin !== undefined) store.dispatch("setDisableGoogleLogin", disableGoogleLogin);
  }, [url, mode]);

  useEffect(() => {
    const microphoneListener = (mute: boolean) => {
      store.dispatch("setMicrophone", mute);
    };
    const appDataListener = (data: { canClose?: boolean; canOpenSettings?: boolean }) => {
      store.dispatch("setWrapperAppData", { canClose: data?.canClose, canOpenSettings: data?.canOpenSettings });
    };
    appMessage.addEventListener("microphone", microphoneListener);
    appMessage.addEventListener("appData", appDataListener);
    return () => {
      appMessage.removeEventListener("microphone", microphoneListener);
      appMessage.removeEventListener("appData", appDataListener);
    };
  }, []);

  useEffect(() => {
    appMessage.getMicrohoneData();
    appMessage.getAppData();
    navigator.mediaDevices?.enumerateDevices().then((data) => logger.log("devices : ", data));
    return () => {
      appMessage.close();
    };
  }, []);

  useEffect(() => {
    appMessage.ready(settings.secret?.remotePassword ?? undefined);
  }, [settings.secret?.remotePassword]);

  useEffect(() => {
    // The bridge exchanges the raw stored layer: a host reading then writing
    // settings back must not bake in transient env/URL values.
    const getListener = () => appMessage.postSettings(getStoredSettings());
    const setListener = (data: Partial<Settings>) => {
      // save already broadcasts via appMessage.postSettings
      const newSettings = setSettings(data, { save: true });
      logger.log("localstorageSetSettings : ", newSettings);
    };
    appMessage.addEventListener("localstorageGetSettings", getListener);
    appMessage.addEventListener("localstorageSetSettings", setListener);
    return () => {
      appMessage.removeEventListener("localstorageGetSettings", getListener);
      appMessage.removeEventListener("localstorageSetSettings", setListener);
    };
  }, [setSettings]);

  // Stable identity (the SDK replays the matching over every source when it changes):
  // only a new destinations config rebuilds it. The extractor itself reads the live
  // meeting store, so it always matches against fresh sessions.
  const retransmissionSrcExtractor = useMemo(() => {
    // A new config deserves a fresh report: an error already toasted for the previous
    // destinations must show again if it survived the edit.
    reportedRetransmissionErrors.clear();
    return buildRetransmissionSrcExtractor(settings.retransmission?.destinations, reportRetransmissionTemplateError);
  }, [settings.retransmission?.destinations]);

  // A pipeline is validated before it can reach a negotiation, and runs guarded
  // once there: a bad one must not be able to kill the publication (the SDK
  // rethrows out of the SDP middleware). A new config deserves a fresh report.
  const sdpFormatters = useMemo(() => {
    reportedSdpFormatterErrors.clear();
    return buildSdpFormatters(settings?.emission?.sdpFormatter, reportSdpFormatterError);
  }, [settings?.emission?.sdpFormatter]);

  // What each published stream — my own medias and the ones this page republishes — reports
  // to the recording chain (presence, camera, mic): pure over the live store, so one instance
  // for the app's lifetime. The SDK hands it to both publishing managers.
  const retransmissionMediaStateExtractor = useMemo(() => buildRetransmissionMediaStateExtractor(), []);

  // Where the media events of each publish attempt go — reporting itself is always on, this
  // only routes it. Unset, the SDK derives the route from the publish host; the override
  // serves stacks where that host does not front the middleware (the local dev stack has no
  // proxy). One middleware, one endpoint for both paths.
  const mediaEventsEndpoint = resolveMediaEventsEndpoint(settings);

  const provideMeetingContext = (children: React.ReactNode) => (
    <MeetingProvider
      context={meetingsContext}
      sdkClient={sdkCtx.client}
      useChatApdapter
      userId={user?.id}
      incomingStreamSessionValidator={(meeting, mySession, incomingSession) => {
        return incomingSession.id !== mySession.id;
      }}
      retransmissionSrcExtractor={retransmissionSrcExtractor}
      retransmissionMediaStateExtractor={retransmissionMediaStateExtractor}
      logs={settings.logs?.meetingContext}
      options={{
        receptionManager: {
          ovenMediaSession: {
            options: {
              retryAfter: 500,
              video: { mode: "subscription", delayToDisableStream: 5000 },
              audio: true,
            },
          },
          logger: settings.logs?.ovenMediaSession?.reception?.main ? logger.log : undefined,
          webrtcLogger: settings.logs?.ovenMediaSession?.reception?.webrtc ? logger.log : undefined,
        },
        emissionManager: {
          sdp: { formatter: sdpFormatters },
          logger: settings.logs?.ovenMediaSession?.emission?.main ? logger.log : undefined,
          webrtcLogger: settings.logs?.ovenMediaSession?.emission?.webrtc ? logger.log : undefined,
          // Routing only: both SDK publishing paths report unless told `enabled: false`. Each
          // attempt dials with its own `clientAttempt` nonce so the middleware tells one
          // attempt of a media from the next.
          mediaEvents: { endpoint: mediaEventsEndpoint },
        },
        retransmissionManager: {
          logger: settings.logs?.ovenMediaSession?.retransmission?.main ? logger.log : undefined,
          webrtcLogger: settings.logs?.ovenMediaSession?.retransmission?.webrtc ? logger.log : undefined,
          mediaEvents: { endpoint: mediaEventsEndpoint },
        },
      }}
    >
      {children}
    </MeetingProvider>
  );

  const privateRoutes = () => {
    if (!mode) return <LoadingView fullscreen />;

    if (mode === SessionMode.SLAVE) {
      return (
        <SlaveRouteContainer element={<PrivateRoute mode={mode} authStatus={authStatus} />}>
          {generateRoute(SessionMode.SLAVE, PageSlaveMode.MEETING, <MeetingSlavePage />)}
          <Route path="" element={<NavigateSlave to={PAGE_PATHS[mode][PageSlaveMode.MEETING]} />} />
          <Route path="*" element={<NavigateSlave to={PAGE_PATHS[mode][PageSlaveMode.MEETING]} />} />
        </SlaveRouteContainer>
      );
    }

    if (!user) return <AppLoadingView />;

    // Guests have no home page: their session is bound to a meeting, so the
    // fallback routes send them back to their lobby (or end the session).
    const isGuest = user.role === ROLE.GUEST;
    const homePath = isGuest ? "/" : PAGE_PATHS[mode][PageMasterMode.LIST];
    const fallbackRoute = isGuest ? <GuestFallbackRoute /> : <AppNavigate to={homePath} />;

    return (
      <MasterRouteContainer element={<PrivateRoute mode={mode} authStatus={authStatus} />}>
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGOUT, <LogoutPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginRedirect defaultTo={homePath} />)}
        {!isGuest && generateRoute(SessionMode.MASTER, PageMasterMode.LIST, <MeetingListStudentPage />)}
        {/* Guests keep no list page, but a /list?vodId= replay link must not
            end their session: play it on /player (registered in this tree)
            instead of falling into the logout catch-all. */}
        {isGuest && <Route path={PAGE_PATHS[SessionMode.MASTER][PageMasterMode.LIST]} element={<ListVodRedirect fallback={<GuestFallbackRoute />} />} />}
        {generateRoute(SessionMode.MASTER, PageMasterMode.JOIN, <JoinPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOBBY, <MeetingLobbyPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.PLAYER, <MeetingPlayerPage />)}
        {compositionRoutes()}
        {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING, provideMeetingContext(<MeetingInitPage />), {
          children: <>{generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_DASHBOARD, <MeetingDashboardStudentPage />)}</>,
        })}
        <Route path="" element={fallbackRoute} />
        <Route path="*" element={fallbackRoute} />
      </MasterRouteContainer>
    );
  };

  const publicRoutes = () => {
    if (!mode) return <LoadingView fullscreen />;
    if (mode === SessionMode.SLAVE || getGlobalQuery(url.href, NavigateGlobalQuery.SLAVE)) {
      return (
        <SlaveRouteContainer element={<PublicRoute mode={mode} authStatus={authStatus} />}>
          <Route path="" element={<PublicRedirect mode={mode} />} />
          <Route path="*" element={<PublicRedirect mode={mode} />} />
        </SlaveRouteContainer>
      );
    }
    return (
      <MasterRouteContainer element={<PublicRoute mode={mode} authStatus={authStatus} />}>
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.FORBIDDEN, <ForbiddenPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.JOIN, <JoinPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOBBY, <MeetingLobbyPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.PLAYER, <MeetingPlayerPage />)}
        {compositionRoutes()}
        {/* In-meeting URLs resolve to the public lobby (pre-join gate) — e.g. a
            guest reloading mid-meeting, whose ephemeral session ended. */}
        <Route path={`${PAGE_PATHS[SessionMode.MASTER][PageMasterMode.MEETING]}/*`} element={<PublicMeetingRedirect />} />
        {/* /list?vodId=<id> deep-links play on the public /player route;
            otherwise the usual logged-out handling (login with a redirect
            back to /list). */}
        <Route path={PAGE_PATHS[SessionMode.MASTER][PageMasterMode.LIST]} element={<ListVodRedirect fallback={<PublicRedirect mode={mode} />} />} />
        <Route path="" element={<PublicRedirect mode={mode} />} />
        <Route path="*" element={<PublicRedirect mode={mode} />} />
      </MasterRouteContainer>
    );
  };

  const routes = () => {
    if (authStatus === "in") {
      if (user) return privateRoutes();
      return (
        <ConnexionView
          status={userFetchStatus}
          retry={() => {
            if (!user) {
              fetchUser();
              return;
            }
          }}
        />
      );
    }
    return publicRoutes();
  };

  // Slave screens are display-only, and impersonating requires being logged in.
  // The bar renders nothing unless the account may actually use it.
  const withImpersonationBar = mode === SessionMode.MASTER && authStatus === "in";
  if (!withImpersonationBar) {
    return (
      <UserCardProvider>
        <div className={styles.background}>{routes()}</div>
      </UserCardProvider>
    );
  }

  return (
    <UserCardProvider>
      <div className={clsx(styles.background, styles.stacked)}>
        <ImpersonationBar />
        <div className={styles.content}>{routes()}</div>
      </div>
    </UserCardProvider>
  );
}

export default App;
